/**
 * 69.35 (the 69.33 finding, sweeps/2026-10-24-69.33-integration-pass.md): a vehicle the motor pool unpacks at the base gets a
 * crew. With the unwrap on (GameConfig.startUnwrapped, a pool standing), a push's attacker starts with its heavy vehicles packed;
 * they unpack in the first minutes, but its arrivals land forward (the push's landing, its relays and FOBs) and its bots are at
 * the front: the tank, the mortar carrier and the launcher technicals stood 24–39 people from its HQ with every seat empty
 * all match, and one gunner alone in a technical with no driver kept the side's crew call (ai/technicalLoop.ts crewWanted)
 * quiet for all of them. So the respawn wave crews them: a fire-support vehicle of the side — a technical of any mount, a
 * tank, a mortar carrier; the missile technical only with ships in play — standing within BASE_CREW.basePeople of its base with its driver's or its gun's seat empty takes
 * the side's next dead bots in those seats (the driver's first), BASE_CREW.perWave a side a wave (match.ts, the waves). The
 * bots' seat brains drive and fight it from there (techDrive, mortarDrive, crewGun). The transports keep the APC loop's
 * rides, the supply truck the convoys' own call, the bike its rider. Pure.
 */
import type { GameState, StructureState } from '../types';
import { BODIES, type BodyKey } from '../constants/body';
import { BASE_CREW } from '../constants/action';
import { arcDistance } from './gameState';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { seatTaken } from './seats';
import { structureTeam } from './body';
import { vehiclesBarred } from './vehicleUse';

/** Is the base crew on: the unwrap with a pool standing (off, the harness's and the stored cases' start: nothing here runs). */
export function baseCrewOn(state: Pick<GameState, 'config' | 'motorPools'>): boolean {
  return !!state.config.startUnwrapped && !!state.motorPools;
}

/** A fire-support vehicle's two crew seats — its driver's and its gun's (a tank's main gun, a mortar carrier's tube, else its first weapon seat); null for any other body. */
export function crewSeats(v: Pick<StructureState, 'type' | 'armament' | 'seats'>): { driver: number; gun: number } | null {
  if (v.type !== 'technical' && v.type !== 'tank' && !(v.type === 'tracked' && v.armament === 'mortar')) return null;
  const defs = BODIES[v.type as BodyKey].seats ?? [];
  const driver = defs.findIndex(d => d.role === 'driver');
  const gun = v.type === 'tank' ? (v.seats ?? []).findIndex(z => !!z.main) : v.armament === 'mortar' ? (v.seats ?? []).findIndex(z => !!z.mortar) : defs.findIndex(d => d.role === 'weapon');
  return driver >= 0 && gun >= 0 && v.seats?.[driver] && v.seats[gun] ? { driver, gun } : null;
}

/** Can a ship fly in this match for a missile to lock (core/antiDropshipMissile.ts lockCandidates: a dropship's flight, a wave ship's sortie, a lighter's run)? */
export function shipsInPlay(state: Pick<GameState, 'dropships' | 'config'>): boolean {
  return !!state.dropships || !!state.config.waveShips || !!state.config.lighters;   // (a wave ship's sortie flies as a dropship's flight, the record made with the first)
}

/** The seat `team`'s next arrival takes: the first empty crew seat (the driver's, then the gun's) of its first standing fire-support vehicle at its base, in the structures' order; null when none wants one. */
export function baseCrewSeat(state: GameState, team: number): { structureId: number; seat: number } | null {
  if (!baseCrewOn(state) || vehiclesBarred(state, team)) return null;
  const base = state.board?.bases.find(b => b.team === team)?.dir ?? state.actionHomes?.[team];
  if (!base) return null;
  const reach = BASE_CREW.basePeople * PERSON_BODY_HEIGHT * personSetOf(state).k;
  for (const v of state.structures) {
    if (v.health <= 0 || !v.drive || structureTeam(state, v) !== team) continue;
    const c = crewSeats(v);
    if (v.armament === 'missile' && !shipsInPlay(state)) continue;   // (its rail fires only at ships: crewed with none, its gunner got out and the next wave was sent in again)
    if (!c || arcDistance(v.surfaceDir, base, state.planets[v.planetIndex].radius) > reach) continue;
    if (!seatTaken(v.seats![c.driver]) && !v.disabled) return { structureId: v.id, seat: c.driver };   // (73.7a: a mobility-killed hull's wheel is no seat: its gun is)
    if (!seatTaken(v.seats![c.gun])) return { structureId: v.id, seat: c.gun };
  }
  return null;
}
